import React from "react";
import { motion } from "framer-motion";
import { BusFront, Users, Wrench, ShieldCheck } from "lucide-react";
import PageWrapper from "../components/PageWrapper";
import PageHero from "../components/PageHero";
import BusImage from "../components/BusImage";

const fleetTypes = [
  {
    name: "Mini School Bus",
    seats: "20–24 Seater",
    description:
      "Compact, agile and ideal for short-distance routes and pre-primary students. Easy to navigate through narrow lanes.",
    badge: "Popular",
  },
  {
    name: "Standard School Bus",
    seats: "32–40 Seater",
    description:
      "The workhorse of our fleet. Comfortable seating, sturdy build, and full safety package for daily school routes.",
    badge: "Most Used",
  },
  {
    name: "Large School Bus",
    seats: "48–56 Seater",
    description:
      "Designed for high-capacity routes with maximum efficiency. Reinforced safety features and trained attendant onboard.",
    badge: "High Capacity",
  },
];

const fleetSpecs = [
  {
    icon: ShieldCheck,
    title: "Safety Equipped",
    points: [
      "Speed governors fitted",
      "CCTV cameras inside cabin",
      "Emergency exits & first-aid kit",
      "Fire extinguishers onboard",
    ],
  },
  {
    icon: Users,
    title: "Trained Crew",
    points: [
      "Background-verified drivers",
      "Female attendants per bus",
      "Annual safety training",
      "Strict conduct guidelines",
    ],
  },
  {
    icon: Wrench,
    title: "Maintenance Standards",
    points: [
      "Daily pre-trip inspections",
      "Scheduled servicing",
      "Fitness certified vehicles",
      "Real-time health monitoring",
    ],
  },
];

const Fleet = () => {
  return (
    <PageWrapper testId="fleet-page">
      <PageHero
        eyebrow={
          <>
            <BusFront size={16} />
            Modern, Maintained, Monitored
          </>
        }
        title={
          <>
            A Fleet Built for
            <br />
            <span className="brand-blue">Student Safety</span>
            <span className="text-amber-500">.</span>
          </>
        }
        description="Every bus in our fleet is selected, equipped and maintained for one purpose — getting students to school safely and on time. From compact mini-buses to large 56-seaters, every vehicle meets our strict safety and operational standards."
        cta={{ label: "Talk to Us", to: "/contact" }}
        rightContent={<BusImage variant="fleet" />}
      />

      {/* Fleet Types */}
      <section
        className="max-w-[1280px] mx-auto px-5 lg:px-10 mb-14"
        data-testid="fleet-types"
      >
        <div className="mb-6">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0F1B2D]">
            Our Vehicle <span className="brand-blue">Lineup</span>
          </h2>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Choose the right vehicle for your routes — we manage everything
            end-to-end.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {fleetTypes.map((bus, i) => (
            <motion.div
              key={bus.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="lift relative bg-white border border-slate-200 rounded-2xl p-7 overflow-hidden"
              data-testid={`fleet-bus-${i}`}
            >
              <div className="absolute top-5 right-5 text-[10px] font-bold tracking-wider px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">
                {bus.badge}
              </div>
              <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mb-5">
                <BusFront size={32} className="text-[#2563EB]" />
              </div>
              <div className="text-sm font-semibold text-[#2563EB] mb-1">
                {bus.seats}
              </div>
              <h3 className="text-xl font-extrabold text-[#0F1B2D] mb-3">
                {bus.name}
              </h3>
              <p className="text-[14px] text-slate-600 leading-relaxed">
                {bus.description}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Fleet Specs */}
      <section
        className="max-w-[1280px] mx-auto px-5 lg:px-10 mb-14"
        data-testid="fleet-specs"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {fleetSpecs.map((spec, i) => (
            <motion.div
              key={spec.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-white border border-slate-200 rounded-2xl p-7 lift"
              data-testid={`fleet-spec-${i}`}
            >
              <div className="w-14 h-14 rounded-full bg-blue-50 text-[#2563EB] flex items-center justify-center mb-4">
                <spec.icon size={26} />
              </div>
              <h3 className="text-lg font-extrabold text-[#0F1B2D] mb-2">
                {spec.title}
              </h3>
              <div className="w-8 h-[3px] bg-amber-400 rounded-full mb-4" />
              <ul className="space-y-2">
                {spec.points.map((p) => (
                  <li
                    key={p}
                    className="flex items-start gap-2 text-[14px] text-slate-700"
                  >
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#2563EB] shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>
    </PageWrapper>
  );
};

export default Fleet;
